import * as _ from 'lodash';
import * as moment from 'moment';
import * as React from 'react';
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { signOut as signOutAction } from "../../actions/loginActions";
import {
  checkUncheck as checkUncheckAction, clearMailData as clearMailDataAction, refreshList as refreshListAction, savePaginationInfo as savePaginationInfoAction,
  setSortFieldAndOrder as setSortFieldAndOrderAction
} from "../../actions/mailActions";
import { addNotification as addNotificationAction, clearNotifications as clearNotificationsAction } from "../../actions/notificationsActions";
import { MailService } from '../../services/mail';
import Notification from '../common/Notification';
import UserSettings from '../settings/model/Settings';
import MessageItemMobile from './MessageItemMobile';
import MessagePreviewMobile from './MessagePreviewMobile';
import Message from "./model/Message";
import MessageDetails from './model/MessageDetails';
import Paging from "./model/Paging";
import { sanitizeHtml } from './util/sanitize';

interface IMessageListMobileProps {
  csrf: string;
  loginTime: number;
  messageList: Message[];
  addNotification: (notification: Notification) => any;
  clearMailData: () => any;
  clearNotifications: () => any;
  checkUncheck: (messages: Message[]) => any;
  refreshList: (messages: Message[]) => any;
  savePaginationInfo: (paginationInfo: Paging) => any;
  signOut: () => any;
  user: string;
  wsid: string;
  sortField: string;
  isDesc: boolean;
  paginationInfo: Paging;
  settings: UserSettings;
}

interface IMessageListMobileState {
  isOpenMap: {};
  detailsMap: {};
}

export class MessageListMobile extends React.Component<IMessageListMobileProps, IMessageListMobileState> {
  public mailService: MailService;

  constructor(props: IMessageListMobileProps) {
    super(props);
    this.mailService = new MailService(this.props.user, this.props.wsid, this.props.csrf);
    const collapsibleMap = {};
    if (this.props.messageList && this.props.messageList.length > 0) {
      for (const message of this.props.messageList) {
        collapsibleMap[message.id] = false;
      }
    }
    this.state = { isOpenMap: collapsibleMap, detailsMap: {} };
    this.getMessageDetails = this.getMessageDetails.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  public componentDidMount() {
    this.props.messageList.forEach((message) => {
      this.getMessageDetails(message.id);
    });

    window.addEventListener("scroll", this.handleScroll);
  }

  public componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  public handleScroll() {
    const windowHeight = "innerHeight" in window ? window.innerHeight : document.documentElement.offsetHeight;
    const body = document.body;
    const html = document.documentElement;
    const docHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
    const windowBottom = windowHeight + window.pageYOffset;

    if (windowBottom >= docHeight) {
      // load new page of messages if any and append to current list
      const numberOfPages = this.props.paginationInfo ? Math.ceil(this.props.paginationInfo.total / this.props.paginationInfo.pageSize) : 0;
      if (this.props.paginationInfo && this.props.paginationInfo.page < numberOfPages - 1) {
        this.getMessageList(this.props.paginationInfo.page + 1, this.props.paginationInfo.pageSize);
        this.props.messageList.forEach((message) => {
          if (!this.state.detailsMap[message.id]) {
            this.getMessageDetails(message.id);
          }
        });
      }
    }
  }

  public async getMessageList(page: number, pageSize: number) {
    try {
      const response = await this.mailService.list(page, pageSize, this.props.isDesc ? 'desc' : 'asc', this.props.sortField);

      if (!response.ok) {
        throw new Error(response.status.toString());
      }

      const msgList = await response.json();
      this.props.savePaginationInfo(msgList.paging); // save pagination info
      const currentMessages = this.props.messageList;
      this.props.refreshList(currentMessages.concat(msgList.messages));
    } catch (e) {
      if (e.message === '401') {
        if (moment().diff(this.props.loginTime, 'hours') < 12) {
          this.props.addNotification({ message: "Session Timeout", isError: true });
        }
        this.props.signOut();
        this.props.clearMailData();
      } else {
        this.props.addNotification({ messageKey: "L.Error.Retrieve", isError: true });
      }
    }
  }

  public async getMessageDetails(messageId: string) {
    try {
      const response = await this.mailService.getDetails(messageId);

      if (!response.ok) {
        throw new Error(response.status.toString());
      }

      const details = await response.json() as MessageDetails;
      const map = this.state.detailsMap;
      const body = details.content.body;
      const plainBody = body.plain ? Buffer.from(body.plain.content, 'base64').toString() : "";
      const htmlBody = body.html ? Buffer.from(body.html.content, 'base64').toString() : plainBody;
      const sanitizedHtml = sanitizeHtml(htmlBody);
      map[messageId] = { htmlBody: sanitizedHtml, attachments: Object.keys(details.content.attachments), showNoImagesBanner: htmlBody.includes('<img') };
      this.setState({ ...this.state, detailsMap: map });
    } catch (e) {
      if (e.message === '401') {
        if (moment().diff(this.props.loginTime, 'hours') < 12) {
          this.props.addNotification({ message: "Session Timeout", isError: true });
        }
        this.props.signOut();
        this.props.clearMailData();
      } else {
        this.props.addNotification({ messageKey: "L.Error.Retrieve", isError: true });
      }
    }
  }

  public toggle(messageId: string, e: React.FormEvent<HTMLInputElement>) {
    if (!(e.target as any).type && (e.target as any).dataset.type !== 'selectCircle') {
      const map = this.state.isOpenMap;
      _.keys(map).filter((id) => id !== messageId.toString()).forEach((id) => map[id] = false);
      map[messageId] = !map[messageId];

      if (!this.state.detailsMap[messageId]) {
        this.getMessageDetails(messageId);
      }

      this.setState({ ...this.state, isOpenMap: map });
    }
  }

  public render() {
    const messageItems = this.props.messageList && this.props.messageList.length > 0
      ? this.props.messageList.map((message, index) => {
          return (
            <div key={message.id} id={message.id} onClick={this.toggle.bind(this, message.id)}>
              <div>
                <MessageItemMobile message={message} />
              </div>
              {this.state.detailsMap[message.id] && <MessagePreviewMobile message={message} isOpen={this.state.isOpenMap[message.id]}
                showNoImagesBanner={this.state.detailsMap[message.id].showNoImagesBanner}
                htmlBody={this.state.detailsMap[message.id].htmlBody}
                attachments={this.state.detailsMap[message.id].attachments} />}
            </div>
          )
        })
      : <p>No messages</p>;

    return (
      <div id="messageListContainer">
        {messageItems}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    csrf: state.authentication.csrf,
    isDesc: state.mail.isDesc,
    loginTime: state.authentication.loginTime,
    messageList: state.mail.messageList,
    paginationInfo: state.mail.paginationInfo,
    settings: state.settings.settings,
    sortField: state.mail.sortField,
    user: state.authentication.user,
    wsid: state.authentication.wsid,
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    addNotification: bindActionCreators(addNotificationAction, dispatch),
    clearMailData: bindActionCreators(clearMailDataAction, dispatch),
    clearNotifications: bindActionCreators(clearNotificationsAction, dispatch),
    checkUncheck: bindActionCreators(checkUncheckAction, dispatch),
    refreshList: bindActionCreators(refreshListAction, dispatch),
    savePaginationInfo: bindActionCreators(savePaginationInfoAction, dispatch),
    setSortFieldAndOrder: bindActionCreators(setSortFieldAndOrderAction, dispatch),
    signOut: bindActionCreators(signOutAction, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MessageListMobile);



// WEBPACK FOOTER //
// ./src/components/mail/MessageListMobile.tsx